import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col, Card, Badge } from "react-bootstrap";
import {
  FiShoppingBag,
  FiClock,
  FiHeart,
  FiDollarSign,
  FiArrowRight,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiTruck,
  FiPackage,
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiStar,
  FiSend,
  FiMessageSquare,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import CustomerMasterLayout from "../../layouts/CustomerMasterLayout";
import Button from "../../components/general/button/Button";
import ActionDropdown from "../../components/customer/dropdown/ActionDropdown";
import "../../assets/css/customer/customerDashboard/CustomerDashboard.css";
import Pagination from "../../components/general/pagination/Pagination";
import MyContext from "../../context/MyContext";
import axios from "axios";
import { CustomerDashboardSkeleton } from "../../components/general/skeleton/SkeletonBlock";

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [ordersPerPage, setOrdersPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [openOrderId, setOpenOrderId] = useState(null);
  const { baseUrl, authToken } = useContext(MyContext);

  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState("");
  const [statsData, setStatsData] = useState({
    total_orders: 0,
    pending_orders: 0,
    wishlist_items: 0,
    total_spent: 0,
  });
  const [allOrders, setAllOrders] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentChats, setRecentChats] = useState([]);

  const getDashboardStats = () => {
    axios
      .get(`${baseUrl}/customer/dashboard`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        const data = response.data;
        setCustomerName(data.customer?.name || "");
        setStatsData({
          total_orders: data.stats?.total_orders ?? 0,
          pending_orders: data.stats?.pending_orders ?? 0,
          wishlist_items: data.stats?.wishlist_items ?? 0,
          total_spent: data.stats?.total_spent ?? 0,
        });
        setAllOrders(data.recent_orders || []);
        setRecentTickets(data.recent_tickets || []);
        setRecentChats(data.recent_chats || []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getDashboardStats();
  }, []);

  const statsCards = [
    {
      icon: FiShoppingBag,
      title: "Total Orders",
      value: statsData.total_orders,
      color: "#097d43",
      bgColor: "#e8f5f0",
    },
    {
      icon: FiClock,
      title: "Pending Orders",
      value: statsData.pending_orders,
      color: "#ffc107",
      bgColor: "#fff8e1",
    },
    {
      icon: FiHeart,
      title: "Wishlist Items",
      value: statsData.wishlist_items,
      color: "#dc3545",
      bgColor: "#ffebee",
    },
    {
      icon: FiDollarSign,
      title: "Total Spent",
      value: `Rs ${Number(statsData.total_spent).toLocaleString()}`,
      color: "#097d43",
      bgColor: "#e8f5f0",
    },
  ];

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = allOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(allOrders.length / ordersPerPage);

  const getStatusBadge = (status) => {
    const map = {
      delivered: { bg: "success", text: "Delivered", icon: FiCheckCircle },
      pending: { bg: "warning", text: "Pending", icon: FiClock },
      processing: { bg: "info", text: "Processing", icon: FiPackage },
      shipped: { bg: "primary", text: "Shipped", icon: FiTruck },
      cancelled: { bg: "danger", text: "Cancelled", icon: FiXCircle },
    };
    const c = map[String(status).toLowerCase()] || map.pending;
    return (
      <Badge bg={c.bg} className="status-badge">
        <c.icon size={12} className="me-1" />
        {c.text}
      </Badge>
    );
  };

  const getPaymentBadge = (status) => {
    const map = {
      paid: { variant: "success", text: "Paid" },
      unpaid: { variant: "warning", text: "Unpaid" },
      "refund pending": { variant: "secondary", text: "Refund Pending" },
    };
    const c = map[String(status).toLowerCase()] || map.unpaid;
    return (
      <Badge bg={c.variant} className="payment-badge">
        {c.text}
      </Badge>
    );
  };

  const getTicketStatusBadge = (status) => {
    const map = {
      Open: "primary",
      "In Progress": "warning",
      Resolved: "success",
      Closed: "secondary",
    };
    return (
      <Badge bg={map[status] || "secondary"} className="status-badge">
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <CustomerMasterLayout>
        <Container fluid className="user-dashboard">
          <CustomerDashboardSkeleton />
        </Container>
      </CustomerMasterLayout>
    );
  }

  return (
    <CustomerMasterLayout>
      <Container fluid className="user-dashboard">
        {/* Welcome */}
        <div className="welcome-section">
          <div className="welcome-content">
            <h2 className="welcome-title">Welcome back, {customerName}! 👋</h2>
            <p className="welcome-subtitle">
              Here's what's happening with your orders today
            </p>
          </div>
          <div className="welcome-date">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </div>
        </div>

        {/* Stats */}
        <Row className="g-3 mb-4">
          {statsCards.map((stat, i) => (
            <Col key={i} xs={12} sm={6} xl={3}>
              <Card className="dash-stats-card">
                <Card.Body>
                  <div className="dash-stats-card-content">
                    <div>
                      <p className="dash-stats-label">{stat.title}</p>
                      <h3 className="dash-stats-value">{stat.value}</h3>
                    </div>
                    <div
                      className="dash-stats-icon-wrapper"
                      style={{ backgroundColor: stat.bgColor }}
                    >
                      <stat.icon size={28} style={{ color: stat.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts Row — Tickets + Recent Chats */}
        <Row className="g-4 mb-4 align-items-stretch">
          {/* Recent Support Tickets */}
          <Col xs={12} lg={8} className="d-flex">
            <Card className="chart-card w-100">
              <Card.Body className="d-flex flex-column">
                <div className="chart-header">
                  <h5 className="section-title pie-section-title mb-0">
                    Recent Support Tickets
                  </h5>
                  <Button
                    variant="outline"
                    size="small"
                    icon={<FiArrowRight size={13} />}
                    iconPosition="right"
                    onClick={() => navigate("/customer/help")}
                  >
                    View All
                  </Button>
                </div>
                <div className="table-responsive">
                  <table className="orders-table tickets-mini-table">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Subject</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Forwarded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTickets.map((t, i) => (
                        <tr key={i}>
                          <td>
                            <span className="order-id">{t.id}</span>
                          </td>
                          <td
                            style={{
                              maxWidth: 130,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t.subject}
                          </td>
                          <td>
                            <span className="order-date">{t.type}</span>
                          </td>
                          <td>{getTicketStatusBadge(t.status)}</td>
                          <td>
                            <span className="order-date">{t.date}</span>
                          </td>
                          <td>
                            {t.forwarded ? (
                              <span className="dash-forwarded-badge">
                                <FiSend size={10} className="me-1" />
                                Yes
                              </span>
                            ) : (
                              <span className="ticket-na">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* ✅ Recent Chats — pie chart ki jagah */}
          <Col xs={12} lg={4} className="d-flex">
            <Card className="chart-card w-100">
              <Card.Body
                className="d-flex flex-column"
                style={{ padding: "1.5rem" }}
              >
                <div className="chart-header">
                  <h5 className="section-title pie-section-title mb-0">
                    Recent Chats
                  </h5>
                  <Button
                    variant="outline"
                    size="small"
                    icon={<FiArrowRight size={13} />}
                    iconPosition="right"
                    onClick={() => navigate("/customer/messages")}
                  >
                    View All
                  </Button>
                </div>

                {/* Chat list */}
                <div className="dash-chats-list">
                  {recentChats.map((chat) => (
                    <div
                      key={chat.id}
                      className="dash-chat-item"
                      onClick={() => navigate(`/customer/messages?shop=${chat.id}`)}
                    >
                      {/* Avatar */}
                      <div className="dash-chat-avatar">
                        <img src={chat.avatar} alt={chat.name} />
                        {chat.online && <span className="dash-chat-online" />}
                      </div>

                      {/* Info */}
                      <div className="dash-chat-info">
                        <div className="dash-chat-top">
                          <span className="dash-chat-name">{chat.name}</span>
                          <span className="dash-chat-time">{chat.time}</span>
                        </div>
                        <div className="dash-chat-bottom">
                          <span className="dash-chat-msg">
                            {chat.lastMessage}
                          </span>
                          {chat.unread > 0 && (
                            <span className="dash-chat-unread">
                              {chat.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Orders */}
        <Row className="g-4">
          <Col xs={12}>
            <Card className="orders-table-card">
              <Card.Body>
                <div className="table-header">
                  <h5 className="section-title pie-section-title mb-0">
                    Recent Orders
                  </h5>
                  <Button
                    variant="outline"
                    size="small"
                    icon={<FiArrowRight size={13} />}
                    iconPosition="right"
                    onClick={() => navigate("/customer/orders")}
                  >
                    View All
                  </Button>
                </div>

                <div className="table-responsive">
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Date</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Payment</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((order, i) => (
                        <tr key={i}>
                          <td>
                            <span className="order-id">{order.id}</span>
                          </td>
                          <td>
                            <span className="order-date">{order.date}</span>
                          </td>
                          <td>
                            <span className="items-count">
                              {order.items} items
                            </span>
                          </td>
                          <td>
                            <span className="order-total">{order.total}</span>
                          </td>
                          <td>{getStatusBadge(order.status)}</td>
                          <td>{getPaymentBadge(order.paymentStatus)}</td>
                          <td>
                            {(() => {
                              // ── single action = icon button, 2+ actions = dropdown (Orders.js pattern) ──
                              const dashOrderActions = [
                                {
                                  label: "View Details",
                                  icon: <FiEye size={15} />,
                                  onClick: () =>
                                    navigate(`/customer/order-details?id=${order.order_id}`),
                                },
                                ...(order.status === "delivered"
                                  ? [
                                      {
                                        label: "Write a Review",
                                        icon: <FiStar size={15} />,
                                        onClick: () =>
                                          navigate(`/customer/order-details?id=${order.order_id}`),
                                      },
                                    ]
                                  : []),
                              ];

                              if (dashOrderActions.length === 1) {
                                const only = dashOrderActions[0];
                                return (
                                  <button
                                    className="orders-single-action-btn"
                                    title={only.label}
                                    onClick={only.onClick}
                                    style={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: "50%",
                                      border: "1px solid #dee2e6",
                                      background: "#fff",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                      color: "#097d43",
                                    }}
                                  >
                                    {only.icon}
                                  </button>
                                );
                              }

                              return (
                                <ActionDropdown
                                  isOpen={openOrderId === order.id}
                                  onToggle={(val) =>
                                    setOpenOrderId(val ? order.id : null)
                                  }
                                  actions={dashOrderActions}
                                />
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-wrapper">
                  <Pagination
                    totalItems={allOrders.length}
                    itemsPerPage={ordersPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(value) => {
                      setCurrentPage(1);
                      setOrdersPerPage(value);
                    }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </CustomerMasterLayout>
  );
};

export default CustomerDashboard;
